const RUSSIAN_TO_LATIN_KEYBOARD: Readonly<Record<string, string>> = {
  й: "q",
  ц: "w",
  у: "e",
  к: "r",
  е: "t",
  н: "y",
  г: "u",
  ш: "i",
  щ: "o",
  з: "p",
  х: "[",
  ъ: "]",
  ф: "a",
  ы: "s",
  в: "d",
  а: "f",
  п: "g",
  р: "h",
  о: "j",
  л: "k",
  д: "l",
  ж: ";",
  э: "'",
  я: "z",
  ч: "x",
  с: "c",
  м: "v",
  и: "b",
  т: "n",
  ь: "m",
  б: ",",
  ю: ".",
};

const LATIN_TO_RUSSIAN_KEYBOARD: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(RUSSIAN_TO_LATIN_KEYBOARD).map(([russian, latin]) => [latin, russian]),
);

const CYRILLIC_TO_LATIN: Readonly<Record<string, string>> = {
  а: "a",
  б: "b",
  в: "v",
  г: "g",
  д: "d",
  е: "e",
  ж: "zh",
  з: "z",
  и: "i",
  й: "i",
  к: "k",
  л: "l",
  м: "m",
  н: "n",
  о: "o",
  п: "p",
  р: "r",
  с: "s",
  т: "t",
  у: "u",
  ф: "f",
  х: "kh",
  ц: "ts",
  ч: "ch",
  ш: "sh",
  щ: "shch",
  ъ: "",
  ы: "y",
  ь: "",
  э: "e",
  ю: "yu",
  я: "ya",
};

export interface SearchQueryVariant {
  value: string;
  weight: number;
}

export function normalizeSearch(value: unknown): string {
  return String(value ?? "")
    .toLocaleLowerCase()
    .replaceAll(",", ".")
    .replaceAll("ё", "е")
    .replaceAll("ı", "i")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\p{L}\p{N}.]+/gu, " ")
    .trim()
    .replace(/\s+/g, " ");
}

export function compactSearch(value: unknown): string {
  return normalizeSearch(value).replace(/[^\p{L}\p{N}]+/gu, "");
}

function convertKeyboardLayout(
  value: string,
  mapping: Readonly<Record<string, string>>,
): string {
  return Array.from(value, (character) => mapping[character] ?? character).join("");
}

export function transliterateCyrillic(value: unknown): string {
  const normalized = normalizeSearch(value);
  return Array.from(
    normalized,
    (character) => CYRILLIC_TO_LATIN[character] ?? character,
  ).join("");
}

export function foldSearchVowels(value: unknown): string {
  return normalizeSearch(value).replaceAll("о", "а").replaceAll("o", "a");
}

export function queryVariants(value: unknown): SearchQueryVariant[] {
  const normalized = normalizeSearch(value);
  if (!normalized) {
    return [];
  }
  const variants = new Map<string, number>();
  const add = (candidate: string, weight: number): void => {
    const prepared = normalizeSearch(candidate);
    if (prepared && weight > (variants.get(prepared) ?? 0)) {
      variants.set(prepared, weight);
    }
  };
  const addWithCompact = (candidate: string, weight: number): void => {
    add(candidate, weight);
    if (!/\d/u.test(candidate)) {
      const compact = compactSearch(candidate);
      if (compact.length >= 2 && compact !== normalizeSearch(candidate)) {
        add(compact, weight * 0.97);
      }
    }
  };

  addWithCompact(normalized, 1);

  const russianLayout = convertKeyboardLayout(
    normalized,
    LATIN_TO_RUSSIAN_KEYBOARD,
  );
  if (russianLayout !== normalized) {
    addWithCompact(russianLayout, 0.94);
  }

  const latinLayout = convertKeyboardLayout(
    normalized,
    RUSSIAN_TO_LATIN_KEYBOARD,
  );
  if (latinLayout !== normalized) {
    addWithCompact(latinLayout, 0.94);
  }

  const transliterated = transliterateCyrillic(normalized);
  if (transliterated !== normalized) {
    addWithCompact(transliterated, 0.92);
  }

  const vowelFolded = foldSearchVowels(normalized);
  if (vowelFolded !== normalized) {
    addWithCompact(vowelFolded, 0.9);
  }

  return Array.from(variants, ([variant, weight]) => ({
    value: variant,
    weight,
  }));
}
